import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Chip } from '@mui/material';
import type { InteractionPart } from '@lib/models/InteractionPart';
import type { Interaction } from '@lib/models/Interaction';
import { AiChatServiceContext } from '../../AiChatContext';
import { useEnvironmentOptional } from '../../../LlmResponseRender/EnvironmentContext';
import FunctionPart from './FunctionPart';
import type { PartRendererProps } from './types';
import {
    parseClientOutput,
    parseUiArgs,
    type UiToolActionContext,
    type UiToolActionEntry,
    type UiToolContext,
} from './uiToolRegistry';

/**
 * The built-in `uiTool` renderer. The library routes every UI render tool here
 * (server flags them `functionDetails.isUi`); this dispatcher then looks up the
 * host's `uiTools` registry by tool name and either renders the matched
 * component or fires the matched action. Unregistered tools (or no registry at
 * all) fall through to `FunctionPart`, preserving the pre-registry behaviour of
 * showing them like a normal tool call.
 *
 * Hosts may still fully override the `uiTool` kind via `partRenderers={{ uiTool }}`
 * — `MessagePart` prefers an explicit override over this default — but the
 * registry is the intended extension point.
 */
const UiToolDispatcher: React.FC<PartRendererProps> = (props) => {
    const { part, message, onGoing, preview } = props;
    const services = useContext(AiChatServiceContext);
    // Resolve env for actions on EVERY uiTool part. Non-throwing: chat surfaces
    // with no EnvironmentProvider get `{}`, so an action's `env.foo?.()` no-ops.
    const env = useEnvironmentOptional();

    const fd = part.functionDetails;
    
    const entry = services?.uiTools?.resolve(fd?.name);

    // No registry / unregistered UI tool → render it as a plain tool call.
    if (!entry) return <FunctionPart {...props} />;

    const callId = fd?.id ?? '';

    // The tool's client-only payload (preferred render source), parsed once and shared by both kinds.
    const clientOutput = parseClientOutput(fd?.clientOutput);

    if (entry.kind === 'action') {
        return (
            <UiToolActionRunner
                entry={entry}
                callId={callId}
                rawArgs={fd?.args}
                response={fd?.response}
                clientOutput={clientOutput}
                parts={message.parts}
                message={message}
                onGoing={onGoing}
                preview={preview}
                env={env}
            />
        );
    }

    const args = entry.parse ? entry.parse(fd?.args) : parseUiArgs(fd?.args);
    if (args == null) return null; // unparseable / guard-rejected → render nothing
    const ctx: UiToolContext = {
        name: entry.name,
        callId,
        response: fd?.response,
        clientOutput,
        parts: message.parts,
        message,
        onGoing,
        preview,
    };
    // NB: callbacks must NOT call hooks directly (they run in this render). Need a
    // hook? Return a component element — that's the normal pattern anyway.
    return <>{entry.render(args, ctx)}</>;
};

interface ActionRunnerProps {
    entry: UiToolActionEntry;
    callId: string;
    rawArgs: unknown;
    response: unknown;
    clientOutput: unknown;
    parts: ReadonlyArray<InteractionPart>;
    message: Interaction;
    onGoing: boolean;
    preview: boolean;
    env: Record<string, unknown>;
}

/**
 * Fires a registered action exactly once per tool-call id, then optionally shows
 * a status chip. It fires on the LIVE render — the streaming `<CurrentMessage>`
 * carries `preview=true` AND the real environment (it sits OUTSIDE the chat's
 * `InertEnvironmentProvider`). Replay-safety comes solely from that inert wrapper:
 * once a turn settles into history, every env callable no-ops, so a re-fired action
 * dead-ends. We must therefore NOT gate on `preview` — `preview=true` IS the live
 * turn, and gating on it skips the one render where the env is real, leaving the
 * action to fire only against the inert (historical) env. Both sync throws and
 * async rejections surface as an error chip.
 */
const UiToolActionRunner: React.FC<ActionRunnerProps> = ({
    entry, callId, rawArgs, response, clientOutput, parts, message, onGoing, preview, env,
}) => {
    const fired = useRef<string | null>(null);
    const [chip, setChip] = useState<{ label: string; ok: boolean } | null>(null);

    useEffect(() => {
        if (fired.current === callId) return; // fire once per tool call
        fired.current = callId;

        // Actions default to `{}` so no-arg tools (e.g. refresh) still fire; an
        // explicit `parse` returning null is treated as "invalid → skip".
        const args = entry.parse ? entry.parse(rawArgs) : (parseUiArgs(rawArgs) ?? {});
        if (args == null) {
            console.warn('[uiTool] action args rejected by parse():', entry.name);
            return;
        }
        const ctx: UiToolActionContext = { name: entry.name, callId, response, clientOutput, parts, message, onGoing, preview, env };
        Promise.resolve()
            .then(() => entry.run(args, ctx))
            .then(() => {
                const label = typeof entry.status === 'function' ? entry.status(args, ctx) : (entry.status ?? null);
                if (label) setChip({ label, ok: true });
            })
            .catch((e) => {
                console.error('[uiTool] action failed:', entry.name, e);
                setChip({ label: 'Ação não pôde ser executada', ok: false });
            });
        // Fire-once per tool-call id; replay re-fires no-op via InertEnvironmentProvider.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callId]);

    if (!chip) return null;
    return (
        <Box sx={{ my: 0.5 }}>
            <Chip size="small" variant="outlined" color={chip.ok ? 'default' : 'error'} label={chip.label} />
        </Box>
    );
};

export default UiToolDispatcher;
